import { forwardRef, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import _ from 'lodash';


// @mui
import {
  Box,
  Button,
  Card, CardActions,
  CardContent,
  CardHeader, Chip,
  Divider,
  FormControl,
  List, ListItem, ListItemIcon, ListItemText,
  Paper, Popover,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, styled, useTheme } from '@mui/material/styles';

import { gsap ,Sine, Quint, Expo, Quad, Back, Elastic, Cubic, Power1, Power2 } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import {
  SvgBadgeRightFill,
  SvgQuestionFill, SvgSmileFill,
  SvgWarnFill,
} from '../SvgIcons';

import { getAssetsUrl } from '../../utils/@helper/file.helper';
import { BackToTop } from '../scroll-to-top';
import FeedbackFormDrawer from '../drawer/feedback/FeedbackFormDrawer';
import { createEvaluationItem } from '../../_db/api.service';
import { toastError, toastSuccess } from '../toastify';
import { getStorage } from '../../utils/storage';
import { ACCESS_TOKEN } from '../../store/types';
import { toggleEvaluationDrawer } from '../../store/actions/feebackActions';
import { useRouter } from '../../hooks/useRoutes';


// registerPlugin
gsap.registerPlugin(ScrollTrigger);

// ==============================|| Click Popover ||============================== //
// 給予評價
function ChatPopover({ existToken }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();

  const { feedback } = useSelector(state => state);

  const { onIcivetAuthorize, onSessionLogin } = useRouter();

  const menuRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [emoji, setEmoji] = useState({});
  const emojis = {
    question: '您對當前頁面或此服務的評價是？',
    list: [
      { id: 1, answer: '糟透了', image: getAssetsUrl('images/emoji-01.gif') },
      { id: 2, answer: '不喜歡', image: getAssetsUrl('images/emoji-02.gif') },
      { id: 3, answer: '普通',   image: getAssetsUrl('images/emoji-03.gif') },
      { id: 4, answer: '喜 歡',  image: getAssetsUrl('images/emoji-04.gif') },
      { id: 5, answer: '非常棒', image: getAssetsUrl('images/emoji-05.gif') },
    ]
  };

  const [score, setScore] = useState({});
  const scores = {
    question: '您願意推薦「中央總務服務平台」給同事嗎？',
    list: []
  };

  const [reason, setReason] = useState({});
  const chips = {
    question: '',
    question1: '請問您推薦的原因是？',
    question2: '請問您不推薦的原因是？',
    rcd: [
      { answer: '頁面美觀' },
      { answer: '資訊豐富且即時' },
      { answer: '內容實用多元' },
      { answer: '快速找到需要的服務' },
      { answer: '搜索功能精準強大' },
      { answer: '操作簡單' },
      { answer: '其他' },
    ],
    nrcd: [
      { answer: '頁面不美觀' },
      { answer: '資訊單薄且過時' },
      { answer: '資訊無幫助' },
      { answer: '找不到所需的服務' },
      { answer: '操作複雜' },
      { answer: '頁面資訊雜亂' },
      { answer: '搜索功能不精準' }, 
      { answer: '其他' },
    ]
  };

  const [opinion, setOpinion] = useState({});
  const opinions = {
    question: '您希望還能夠滿足您哪些功能需求？（非必填）',
    list: []
  };

  const handleEmoji = (A) => {
    setEmoji({
      question: emojis.question,
      answer: A,
      orderNo: 0
    })
  };

  const handleScore = (A) => {
    setScore({
      question: scores.question,
      answer: A,
      orderNo: 1
    });
    setReason({
      question: '',
      answer: '',
      orderNo: 2
    });
  };

  const handleChip = (A) => {
    setReason({
      question: score?.answer <= 5 ? chips.question2 : chips.question1,
      answer: A,
      orderNo: 2
    })
  };

  const handleOpinion = (A) => {
    setOpinion({
      question: opinions.question,
      answer: A,
      orderNo: 3
    })
  };

  const handleOpen = (event) => {
    if(existToken) {
      setAnchorEl(event.currentTarget);
      dispatch(toggleEvaluationDrawer(true));
      return;
    };
    onSessionLogin();
  };

  const handleClose = () => {
    setAnchorEl(null);
    dispatch(toggleEvaluationDrawer(false));
    setEmoji({});
    setScore({});
    setReason({});
    setOpinion({});
  };


  const handleSubmit = async () => {
    await setIsSubmitting(true);
    const { success, message } = await createEvaluationItem([
      emoji,
      score,
      reason,
      opinion,
    ]);
    if(success) {
      toastSuccess(message);
      await handleClose();
      await setIsSubmitting(false);
      return
    };
    await setIsSubmitting(false);
    toastError(message);
  }

  return ( 
    <>
      <Box ref={menuRef} open={Boolean(feedback?.isOpenEvaluation)} onClick={handleOpen} sx={{ ...theme.typography.hoverPrimaryLeftline2px }}>
        <Typography className={'Sim-question-menus-text'} display={'flex'} gap={0.5} alignItems={'center'} py={1} pl={1} pr={1.5} whiteSpace={'nowrap'} variant={'subtitle1'} fontWeight={700}>
          <SvgSmileFill />
          {/* 給予評價 */}
          {t('cgam.give.feedback')}
        </Typography>
      </Box>

      <Popover
        // disableScrollLock
        anchorEl={menuRef.current}
        open={Boolean(feedback?.isOpenEvaluation)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 238,
        }}
        PaperProps={{
          // onMouseEnter: handleOpen,
          // onMouseLeave: handleClose,
          sx: {
            maxWidth: 366,
            pointerEvents: 'auto',
            borderRadius: '4px',
          },
        }}
      >
        <Card>
          <CardHeader title={'感謝您使用中央總務服務平台，您的反饋讓我們變得更好！'} sx={{ py: 1.5, px: 3, fontSize: 18 }} />
          <CardContent sx={{ p: 3,  borderTop: `1px solid ${theme.palette.divider}`, borderBottom: `1px solid ${theme.palette.divider}`}}>
            <Stack spacing={3}>

              {/* 評價 */}
              <Box>
                <Typography variant={'body1'} fontWeight={700} mb={2}>{emojis.question}</Typography>
                <List disablePadding dense={false} sx={{ display: 'flex' }}>
                  {
                    _.map(emojis.list, (item, index) => {
                      return (
                        <ListItem key={index} disablePadding sx={{ flexDirection: 'column', cursor: 'pointer' }} onClick={() => handleEmoji(item?.answer)}>
                          <ListItemIcon sx={{ minWidth: 32, width: 32, height: 32 }}>
                            <Box
                              component={'img'}
                              src={item?.image}
                              sx={{ filter: `grayscale(${emoji?.answer !== item?.answer && emoji?.answer ? 100 : 0}%)` }}
                            />
                          </ListItemIcon>
                          <ListItemText disableTypography primary={<Typography variant={'body2'} color={emoji?.answer === item?.answer ? theme.palette.color.primaryBlue2 : 'text.secondary'} fontWeight={emoji?.answer === item?.answer ? 700 : 400}>{item?.answer}</Typography>}  />
                        </ListItem>
                      )
                    })
                  }
                </List>
              </Box>
              
              {
                emoji?.answer && (
                  <>
                    <Divider component="div" />

                    {/* 推薦 */}
                    <Box>
                      <Typography variant={'body1'} fontWeight={700} mb={2}>{scores?.question}</Typography>
                      <List disablePadding dense={false} sx={{ display: 'flex', border: `1px solid ${theme.palette.divider}` }}>
                        {
                          _.map(Array.from({ length: 10 }), (item, index) => {
                            return (
                              <ListItem
                                key={index}
                                disablePadding
                                aria-selected={index + 1 === score?.answer}
                                sx={{
                                  ...theme.typography.body2,
                                  color: theme.palette.text.secondary,
                                  textAlign: 'center',
                                  cursor: 'pointer',
                                  transition: 'all 0.3s ease',
                                  '&:not(:last-of-type)': {
                                    '&:after': {
                                      content: '""',
                                      width: '1px',
                                      height: 30,
                                      display: 'block',
                                      position: 'absolute',
                                      top: 0,
                                      bottom: 0,
                                      right: 0,
                                      backgroundColor: theme.palette.divider,
                                    }
                                  },
                                  '&:hover, &[aria-selected=true]': {
                                    fontWeight: 700,
                                    color: theme.palette.color.primaryBlue2,
                                    backgroundColor: theme.palette.primary.lighter
                                  },
                                }}
                                onClick={() => handleScore(index + 1)}
                              >
                                <ListItemText
                                  disableTypography
                                  primary={index + 1}
                                />
                              </ListItem>
                            )
                          })
                        }
                      </List>
                      <Stack direction={'row'} justifyContent={'space-between'} mt={0.5}>
                        <Typography 
                          variant='body2' 
                          color={score?.answer === 1 ? theme.palette.color.primaryBlue2 : theme.palette.text.secondary}
                          fontWeight={score?.answer === 1 ? 700 : 400}
                        >
                          完全不推薦
                        </Typography>
                        <Typography 
                          variant='body2' 
                          color={score?.answer === 10 ? theme.palette.color.primaryBlue2 : theme.palette.text.secondary}
                          fontWeight={score?.answer === 10 ? 700 : 400}
                        >
                          肯定會推薦
                        </Typography>
                      </Stack>
                    </Box>

                    <Divider component="div" />

                    {/* 原因 */}
                    <Box>
                      <Typography variant={'body1'} fontWeight={700} mb={2}>{score?.answer <= 5 ? chips.question2 : chips.question1}</Typography>
                      <List disablePadding dense={false} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {
                          _.map(score?.answer && score?.answer <= 5 ? chips.nrcd : chips.rcd, (item, index) => {
                            return (
                              <ListItem key={index} disablePadding sx={{ width: 'auto', cursor: 'pointer' }}>
                                <Typography 
                                  onClick={() => handleChip(item?.answer)}  
                                  sx={{ 
                                    ...theme.typography.body1,
                                    borderRadius: 0,
                                    color: theme.palette.text.secondary,
                                    backgroundColor: theme.palette.grey[300],
                                    padding: theme.spacing(0.25, 1),
                                    ...(reason?.answer === item?.answer ? {
                                      color: theme.palette.color.primaryBlue2,
                                      fontWeight: 700,
                                      backgroundColor: theme.palette.action.hover,
                                    } : {}),
                                  }} 
                                >{item?.answer}</Typography>
                              </ListItem>
                            )
                          })
                        }
                      </List>
                    </Box>

                    <Divider component="div" />

                    {/* 意見 */}
                    <Box>
                      <Typography variant={'body1'} fontWeight={700} mb={2}>{opinions.question}</Typography>
                      <FormControl fullWidth>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          maxLength={150}
                          inputProps={{ maxLength: 150 }}
                          value={opinion?.answer}
                          onInput={(event) => handleOpinion(event?.target?.value)}
                          placeholder='請輸入內容'
                        />
                        <Stack
                          direction={'row'}
                          alignItems={'center'}
                          position={'absolute'}
                          right={4}
                          top={98}
                        >
                          <Typography variant='body2' color={opinion?.answer && (Number(opinion?.answer?.length >= 0) ) ? 'color.secondaryMain' : 'text.secondary'}>150/{opinion?.answer ? 150 - opinion?.answer?.length : 150}</Typography>
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M17.7054 11.8399C17.5187 11.6526 17.2654 11.5474 17.0013 11.5474C16.7372 11.5474 16.4839 11.6526 16.2972 11.8399L11.8742 16.2825C11.7791 16.3746 11.7032 16.4848 11.651 16.6067C11.5988 16.7286 11.5713 16.8597 11.5702 16.9923C11.569 17.125 11.5943 17.2565 11.6443 17.3793C11.6944 17.5021 11.7684 17.6136 11.8619 17.7074C11.9554 17.8012 12.0666 17.8754 12.189 17.9256C12.3114 17.9759 12.4425 18.0011 12.5748 18C12.707 17.9988 12.8377 17.9713 12.9592 17.9189C13.0807 17.8665 13.1906 17.7904 13.2825 17.695L17.7084 13.2555C17.8011 13.1625 17.8746 13.052 17.9246 12.9305C17.9747 12.8089 18.0003 12.6786 18 12.5471C17.9997 12.4156 17.9736 12.2854 17.923 12.164C17.8725 12.0427 17.7985 11.9325 17.7054 11.8399Z" fill="#333333"/>
                            <path d="M17.7054 11.8399C17.5187 11.6526 17.2654 11.5474 17.0013 11.5474C16.7372 11.5474 16.4839 11.6526 16.2972 11.8399L11.8742 16.2825C11.7791 16.3746 11.7032 16.4848 11.651 16.6067C11.5988 16.7286 11.5713 16.8597 11.5702 16.9923C11.569 17.125 11.5943 17.2565 11.6443 17.3793C11.6944 17.5021 11.7684 17.6136 11.8619 17.7074C11.9554 17.8012 12.0666 17.8754 12.189 17.9256C12.3114 17.9759 12.4425 18.0011 12.5748 18C12.707 17.9988 12.8377 17.9713 12.9592 17.9189C13.0807 17.8665 13.1906 17.7904 13.2825 17.695L17.7084 13.2555C17.8011 13.1625 17.8746 13.052 17.9246 12.9305C17.9747 12.8089 18.0003 12.6786 18 12.5471C17.9997 12.4156 17.9736 12.2854 17.923 12.164C17.8725 12.0427 17.7985 11.9325 17.7054 11.8399Z" fill="black" fillOpacity="0.2"/>
                            <path d="M16.6019 6.29249C16.4152 6.10521 16.1619 6 15.8978 6C15.6337 6 15.3804 6.10521 15.1937 6.29249L6.34075 15.1726C6.23799 15.2626 6.15476 15.3728 6.09625 15.4965C6.03773 15.6201 6.00517 15.7545 6.00057 15.8913C5.99597 16.0281 6.01943 16.1643 6.06951 16.2916C6.1196 16.4189 6.19523 16.5346 6.29171 16.6313C6.38819 16.7281 6.50345 16.804 6.63037 16.8542C6.75729 16.9044 6.89315 16.928 7.02951 16.9234C7.16588 16.9188 7.29984 16.8861 7.4231 16.8274C7.54635 16.7687 7.65625 16.6852 7.74601 16.5821L16.5989 7.70207C16.7851 7.51525 16.89 7.26207 16.8905 6.9979C16.8911 6.73374 16.7873 6.4801 16.6019 6.29249Z" fill="#333333"/>
                            <path d="M16.6019 6.29249C16.4152 6.10521 16.1619 6 15.8978 6C15.6337 6 15.3804 6.10521 15.1937 6.29249L6.34075 15.1726C6.23799 15.2626 6.15476 15.3728 6.09625 15.4965C6.03773 15.6201 6.00517 15.7545 6.00057 15.8913C5.99597 16.0281 6.01943 16.1643 6.06951 16.2916C6.1196 16.4189 6.19523 16.5346 6.29171 16.6313C6.38819 16.7281 6.50345 16.804 6.63037 16.8542C6.75729 16.9044 6.89315 16.928 7.02951 16.9234C7.16588 16.9188 7.29984 16.8861 7.4231 16.8274C7.54635 16.7687 7.65625 16.6852 7.74601 16.5821L16.5989 7.70207C16.7851 7.51525 16.89 7.26207 16.8905 6.9979C16.8911 6.73374 16.7873 6.4801 16.6019 6.29249Z" fill="black" fillOpacity="0.2"/>
                          </svg>
                        </Stack>
                      </FormControl>
                    </Box>
                  </>
                )
              }
            </Stack>
          </CardContent>
          <CardActions sx={{ justifyContent: 'flex-end', px: 3, py: 2 }}>
            <Button variant={'outlined'} color={'inherit'} onClick={() => handleClose()}>取消</Button>
            <Button 
              variant={'contained'}
              disabled={!Boolean(emoji?.answer && score?.answer && reason?.answer && !isSubmitting)}  
              onClick={() => handleSubmit()} 
              sx={{ backgroundColor: theme.palette.color.primaryBlue2 }}
            >
              確認送出
            </Button>
          </CardActions>
        </Card>
      </Popover>
    </>
  );
}


/* =========================================================================== */

const Support = forwardRef(() => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigator = useNavigate();

  const { onIcivetAuthorize, onSessionLogin } = useRouter();

  const { account: { profile } } = useSelector(
    (state) => state
  );

  const existToken = getStorage(ACCESS_TOKEN);
  
  useEffect(() => {
    gsap.set('.Sim-question-to-top', {
      opacity: 0,
      height: 0,
    });
    gsap.to('.Sim-question-to-top', {
      opacity: 1, 
      height: 'auto',
      autoAlpha: 1, 
      scrollTrigger: {
        trigger: "body",
        start: "top -20%",
        end: "top -20%",
        toggleActions: "play none reverse none"
      }
    });
  }, []);

  return (
    <Box
      display={'flex'}
      flexDirection={'column'}
      position={'fixed'}
      bottom={24}
      right={0}
      zIndex={4}
      borderRadius={'4px 0px 0px 4px'}
      borderTop={`1px solid rgba(165, 214, 255, 0.32)`}
      borderBottom={`1px solid rgba(165, 214, 255, 0.32)`}
      borderLeft={`1px solid rgba(165, 214, 255, 0.32)`}
      boxShadow={`0px 6px 8px 0px rgba(0, 0, 0, 0.12)`}
      sx={{
        backgroundColor: 'info.main',
        '& .Sim-question-inner': {
          p: 2,
          '& .Sim-question-content': {
            opacity: 0,
            visibility: 'hidden',
            display: 'flex',
            minWidth: 128,
            // height: '100%',
            // height: 152,
            transform: 'scaleZ(0)',
            transformOrigin: '0 0 0',
            position: 'absolute',
            top: 0,
            right: '100%',
            paddingRight: 0.5,
            alignItems: 'center',
            transition: 'all 0.2s ease',
            '& .Sim-question-menus': {
              width: '100%',
              height: '100%',
              minHeight: 100,
              padding: '12px 8px',
              '& .Sim-question-menus-text': {
                cursor: 'pointer',
                transition: 'all 300ms ease',
                '& svg': {
                  transition: 'all 300ms ease',
                },
                '&:hover': {
                  color: theme => theme.palette.color.primaryBlue3,
                  backgroundColor: theme => theme.palette.action.hover,
                  '& svg': {
                    fill: theme => theme.palette.color.primaryBlue3,
                    color: theme => theme.palette.color.primaryBlue3,
                  }
                }
              },
            },
            '& svg': {
              marginLeft: '-1px',
            }
          },
          '&:hover': {
            '& .Sim-question-content': {
              opacity: 1,
              visibility: 'visible',
              transform: 'scaleZ(1)',
            },
          }
        },
      }}
    >
      <Box className='Sim-question-inner'>
        <SvgQuestionFill color={'common.white'} />
        <Typography
          variant={'subtitle1'}
          fontWeight={700}
          color={'common.white'}
          letterSpacing={8}
          mt={1}
          sx={{ writingMode: 'vertical-rl' }}
        >
          {/* 遇到問題 */}
          {t('cgam.encountered.problem')}
        </Typography>
        <Box className={'Sim-question-content'}>
          <Paper className={'Sim-question-menus'}>
            <Stack spacing={0.5}>
              <Box 
                sx={{ ...theme.typography.hoverPrimaryLeftline2px, textDecoration: 'auto' }} 
                color={'text.primary'}
                onClick={() => existToken ? navigator(`/honhai/faqs`) : onSessionLogin()}
              >
                <Typography className={'Sim-question-menus-text'} display={'flex'} gap={0.5} alignItems={'center'} py={1} pl={1} pr={1.5} whiteSpace={'nowrap'} variant={'subtitle1'} fontWeight={700}>
                  <SvgWarnFill />
                  {/* 常見問題 */}
                  {t('cgam.FAQS')}
                </Typography>
              </Box>

              <FeedbackFormDrawer existToken={existToken}  profile={profile} />
              <ChatPopover existToken={existToken} profile={profile} />
            </Stack>
          </Paper>
          <SvgBadgeRightFill />
        </Box>
      </Box>
      <Box overflow={'hidden'} className={'Sim-question-to-top'}>
        <BackToTop p={2} borderTop={`1px solid ${alpha(theme.palette.secondary.dark, 0.72)}`} />
      </Box>
    </Box>
  )
});

export default Support;
